// Kampf am Ufer (§6): Grundbausteine, die alle Teile des Kampfs teilen (Zustand, Hilfen, Schaden, Heilung, Angriff).
// Aufgeteilt aus battle.ts (CLAUDE.md: Dateien über etwa 1500 Zeilen aufteilen).
import {
  classTrait,
  getAbility,
  getTrait,
  systemOn,
  type CombatRules,
  type StormLevel,
} from '../../content/index.ts';
import { type Rng } from '../rng.ts';
import type {
  BattleCommand,
  BattleEvent,
  BattleSetup,
  Frame,
  Outcome,
  Shot,
  StatusName,
  Unit,
} from '../types.ts';
import { pushOut as outOfTerrain, steer } from './terrain.ts';
import { launchHit, rangedArt } from './shots.ts';

export type Battle = {
  rules: CombatRules;
  storm: StormLevel;
  setup: BattleSetup;
  rng: Rng;
  t: number;
  units: Unit[];
  events: BattleEvent[];
  frames: Frame[];
  recordFrames: boolean;
  rainMultiplier: number;
  /** K54: seit wann nur noch wenige auf jeder Seite stehen (Kräftemessen), sonst null */
  duelSince: number | null;
  nextGust: number;
  /** K21: laufende Böe, Geschwindigkeit für einen mittelschweren Freund */
  gust: { vx: number; vy: number; until: number } | null;
  /** K21 (Fabi): Richtung der letzten Böe; die nächste dreht ein Stück weiter */
  gustAngle: number | null;
  nextLightning: number;
  strikes: { x: number; y: number; radius: number; damage: number; at: number }[];
  friendsStarted: number;
  farewells: number;
  /** K15: Der Landtrupp zieht sich gerade zurück */
  retreating: boolean;
  outcome: Outcome | null;
  /** Fabi (Phase Kern): Herkunft für Zustände und Heilungen, solange ein Kniff läuft ('kniff:<id>') */
  via: string | null;
  /** Nusshagel (Fabi, Phase Kern): Nüsse, die gerade vom Ziel zu einem Freund abprallen */
  bounces: { at: number; src: number; from: number; to: number; amount: number; via?: string }[];
  /** K47: Geschosse im Flug */
  shots: Shot[];
  nextShotId: number;
  /** K48: Kennung der nächsten Ansage */
  nextTelegraphId: number;
  /** K46: Befehle, die noch kommen, nach Takt sortiert */
  commands: BattleCommand[];
};

export const EPS = 1e-9;
/** Zahl einer Fähigkeit für genau dieses Wesen: erst seine eigenen Werte (Level, Items, F35), dann abilities.json */
export const param = (u: Unit | null | undefined, abilityId: string, key: string): number => {
  const own = u?.params[`${abilityId}.${key}`];
  if (own !== undefined) return own;
  const v = getAbility(abilityId).params[key];
  if (v === undefined) throw new Error(`Parameter fehlt: ${abilityId}.${key}`);
  return v;
};
/** Wie param, aber 0, wenn die Fähigkeit den Wert nicht kennt (für Zusatzwerte aus Level-Knoten) */
export const optParam = (u: Unit | null | undefined, abilityId: string, key: string): number =>
  u?.params[`${abilityId}.${key}`] ?? getAbility(abilityId).params[key] ?? 0;
export const perk = (u: Unit | null | undefined, key: string): number => u?.perks[key] ?? 0;

// ---------- Hilfen ----------

export const unitById = (b: Battle, uid: number | null): Unit | undefined => (uid === null ? undefined : b.units[uid]);
export const dist = (a: { x: number; y: number }, c: { x: number; y: number }) => Math.hypot(a.x - c.x, a.y - c.y);
export const gap = (a: Unit, c: Unit) => dist(a, c) - a.radius - c.radius;
export const round = (n: number) => Math.round(n * 100) / 100;

export function emit(b: Battle, e: BattleEvent): void {
  b.events.push(e);
}

/** Kann man ihn gerade angreifen? Eichhörnchen auf dem Baum und eingerollte Igel nicht (S5). */
export function targetable(b: Battle, u: Unit): boolean {
  // K46: herbeigewinkt oder unter der Erde unterwegs (safe) und im Sturzflug ist er nicht anzugreifen
  return u.alive && !u.onTree && u.rolledUntil <= b.t + EPS && u.burrowUntil <= b.t + EPS && !u.travel?.safe && !u.swoop;
}

/** Steht auf dem Feld und kann von Böen und Blitzen getroffen werden (K21: nur Freunde, keine Objekte) */
export function exposedFriend(u: Unit): boolean {
  return u.alive && u.kind === 'friend' && !u.onTree;
}

export function enemiesOf(b: Battle, u: Unit): Unit[] {
  return b.units.filter((o) => o.side !== u.side && targetable(b, o));
}

export function nearest(u: Unit, list: Unit[]): Unit | undefined {
  let best: Unit | undefined;
  let bestD = Infinity;
  for (const o of list) {
    const d = gap(u, o);
    if (d < bestD - EPS) {
      best = o;
      bestD = d;
    }
  }
  return best;
}

/** Fabi (Phase Kern): Jeder Zustand nennt, woher er kommt; ohne Angabe der Kniff, der gerade läuft */
export function setStatus(b: Battle, u: Unit, status: StatusName, until: number, via?: string): void {
  const how = via ?? b.via ?? undefined;
  emit(b, { t: round(b.t), type: 'status', unit: u.uid, status, until: round(until), ...(how ? { via: how } : {}) });
}

/** Woher eine Sonderwirkung eines Wesens kommt, für den Verlauf (mehrere Quellen mit | getrennt) */
export function viaOf(u: Unit | null | undefined, perkKey: string): string | undefined {
  const list = u?.perkFrom[perkKey];
  return list && list.length ? list.join('|') : undefined;
}

export function nextRetarget(b: Battle): number {
  const p = getTrait('unruhig').params;
  return b.t + p.retargetMin! + b.rng.next() * (p.retargetMax! - p.retargetMin!);
}

export function speedFactor(b: Battle, u: Unit): number {
  let f = 1;
  if (u.slowUntil > b.t) f *= 1 - u.slowFactor;
  if (u.hasteUntil > b.t) f *= u.hasteFactor;
  return f;
}

export function moveToward(b: Battle, u: Unit, x: number, y: number, speedScale = 1): void {
  // K50: Wer in der Mulde feststeckt, kommt nicht vom Fleck
  if (u.stuckUntil > b.t + EPS) return;
  const d = Math.hypot(x - u.x, y - u.y);
  if (d < EPS) return;
  const step = Math.min(d, u.moveSpeed * speedScale * speedFactor(b, u) * b.rules.tickSeconds);
  let sx = ((x - u.x) / d) * step;
  let sy = ((y - u.y) / d) * step;
  // K49: an Steinen und dem Stamm entlanggleiten
  if (b.setup.obstacles?.length) [sx, sy] = steer(b.setup.obstacles, u, sx, sy);
  // K48: Wer von selbst handelt, läuft nicht in den Streifen eines angesagten Rammstoßes; ein Befehl darf hindurch
  if (b.setup.telegraphs && u.side === 'friend' && u.order?.kind !== 'move' && !inStripe(b, u.x, u.y, u.radius) && inStripe(b, u.x + sx, u.y + sy, u.radius)) return;
  u.x += sx;
  u.y += sy;
}

/** K48: Liegt der Punkt (mit Radius) im Streifen eines angesagten Angriffs? */
export function inStripe(b: Battle, x: number, y: number, r: number): boolean {
  for (const o of b.units) {
    const w = o.windup;
    if (!w || !o.alive || w.shape.kind !== 'stripe') continue;
    const sh = w.shape;
    const along = (x - sh.x) * sh.dx + (y - sh.y) * sh.dy;
    const across = Math.abs((x - sh.x) * sh.dy - (y - sh.y) * sh.dx);
    if (along >= -r && along <= sh.length + r && across <= sh.width / 2 + r) return true;
  }
  return false;
}

export function clampToField(b: Battle, u: Unit): void {
  const f = b.rules.field;
  u.x = Math.min(f.width - u.radius, Math.max(u.radius, u.x));
  u.y = Math.min(f.height - u.radius, Math.max(u.radius, u.y));
  // K49: niemand steht in einem Stein oder im Stamm
  if (b.setup.obstacles?.length && u.kind !== 'tree') {
    outOfTerrain(b.setup.obstacles, u);
    u.x = Math.min(f.width - u.radius, Math.max(u.radius, u.x));
    u.y = Math.min(f.height - u.radius, Math.max(u.radius, u.y));
  }
}

// ---------- Schaden und Heilung ----------

export type HitKind = 'hit' | 'kniff' | 'poison' | 'lightning' | 'reflect' | 'recoil' | 'splash' | 'dot' | 'thistle';

/** K18: Ab 60 s wird jeder Treffer alle 5 s stärker, auf beiden Seiten [ZAHL?] */
export function rainMultiplierAt(rules: CombatRules, t: number): number {
  const r = rules.heavyRain;
  if (t + EPS < r.startSeconds) return 1;
  return 1 + r.stepBonus * (Math.floor((t - r.startSeconds + EPS) / r.stepSeconds) + 1);
}

/**
 * K54 (Erste Fassung, Auftrag des Projekt-Leads: Prügeleien zwischen zähen Freunden und Gegnern dauern kürzer): Stehen
 * nach fromSecond auf jeder Seite nur noch höchstens maxEach, beginnt nach afterSeconds ein Kräftemessen, und alle stepSeconds werden die
 * Treffer um stepBonus härter, für beide Seiten gleich [ZAHL?]
 */
export function duelMultiplier(b: Battle): number {
  const d = b.rules.duel;
  if (!d) return 1;
  if (b.duelSince === null) {
    if (b.t + EPS < d.fromSecond) return 1;
    const count = (side: string) => b.units.filter((u) => u.side === side && u.alive && u.kind !== 'tree').length;
    if (count('friend') > d.maxEach || count('enemy') > d.maxEach || count('friend') === 0 || count('enemy') === 0) return 1;
    b.duelSince = b.t;
    emit(b, { t: round(b.t), type: 'duel', at: round(b.t + d.afterSeconds) });
  }
  const left = b.t - b.duelSince - d.afterSeconds;
  if (left + EPS < 0) return 1;
  return 1 + d.stepBonus * (Math.floor((left + EPS) / d.stepSeconds) + 1);
}

export function outgoingFactor(b: Battle, u: Unit): number {
  // K23 Wütend: macht eine Weile mehr Schaden
  return u.enragedUntil > b.t ? getTrait('wuetend').params.damageDealtFactor! : 1;
}

/** opts (K51, K52): crit erzwingt einen Volltreffer, noMark lässt eine Schwachstelle stehen */
export function dealDamage(b: Battle, src: Unit | null, tgt: Unit, raw: number, kind: HitKind, via?: string, opts: { crit?: boolean; noMark?: boolean } = {}): void {
  if (!tgt.alive) return;
  // K46: Wer herbeigewinkt wird oder sich unter der Erde zum Bau wühlt, nimmt unterwegs keinen Schaden
  if (tgt.travel?.safe) return;
  // Fabi (Phase Kern): Der Verlauf nennt bei jedem Schaden seinen Grund; ein Kniff-Treffer kommt vom Kniff des Angreifers
  const how = via ?? (kind === 'kniff' && src?.kniff ? `kniff:${src.kniff}` : undefined);
  const isHit = kind === 'hit' || kind === 'kniff';
  let amount = raw;
  let bonus = 0;
  let weakSpot = false;
  // K25: Volltreffer, Chance je nach Klasse oder Eigenschaft
  let crit = false;
  // F35 Zielwurf (Fabi): Volltreffer können mehr Schaden machen
  const critBonus = b.rules.crit.damageBonus + (src ? perk(src, 'critDamage') : 0);
  // I25 Spechtfeder (Fabi, Runde 7): Jeder vierte Angriff ist sicher ein Volltreffer
  let sure = false;
  if (kind === 'hit' && src && perk(src, 'critEvery') > 0 && ++src.critCount >= Math.round(perk(src, 'critEvery'))) {
    src.critCount = 0;
    sure = true;
  }
  if (isHit && src && (opts.crit || sure || (src.critChance > 0 && b.rng.next() < src.critChance))) {
    crit = true;
    amount *= 1 + critBonus;
    // I25 Flusskiesel (Fabi, Runde 7): Jeder Volltreffer füllt den Mut ein Stück
    if (perk(src, 'critMut') > 0) gainMut(b, src, src.mutMax * perk(src, 'critMut'));
  }
  const beforeMark = amount;
  // S5 Eisvogel: Die Schwachstelle macht den nächsten Treffer stärker, egal von wem
  // R27 Stoßtauchen: Solange der Eisvogel unterwegs ist, löst nur er seine Markierung aus
  // K25 (Fabi, Spieltest Phase 4): Ein Treffer auf eine Schwachstelle ist immer ein Volltreffer, mit dem Bonus der
  // Schwachstelle; ein gewürfelter Volltreffer kommt nicht noch obendrauf, es zählt der größere Bonus
  // K51 (Fabi, 26.10.2026): Diese Schwachstelle bleibt, bis ein Kniff sie trifft; normale Angriffe lassen sie stehen.
  // Der Kniff, der sie trifft, trifft dann mit allen seinen Treffern doppelt so hart und immer als Volltreffer
  let spotOpen = false;
  if (isHit && tgt.marked && tgt.markHold) {
    if (kind === 'kniff' && src && src.side !== tgt.side && !opts.noMark && !tgt.markClaim) {
      tgt.markClaim = { by: src.uid, until: src.spin ? src.spin.until : b.t };
      spotOpen = true;
    }
    if (kind === 'kniff' && src && tgt.markClaim && tgt.markClaim.by === src.uid && b.t <= tgt.markClaim.until + EPS && !opts.noMark) {
      const marker = unitById(b, tgt.markedBy);
      const base = crit ? amount / (1 + critBonus) : amount;
      const total = base * (1 + param(marker, 'schwachstelle-ausmachen', 'bonusDamage'));
      bonus = Math.max(0, total - amount);
      amount = total;
      crit = true;
      weakSpot = true;
    }
  } else if (isHit && tgt.marked && (tgt.markReservedBy === null || tgt.markReservedBy === src?.uid)) {
    const marker = unitById(b, tgt.markedBy);
    const base = crit ? amount / (1 + critBonus) : amount;
    const weakBonus = param(marker, 'schwachstelle', 'bonusDamage');
    const total = base * (1 + Math.max(weakBonus, critBonus));
    bonus = Math.max(0, total - amount);
    amount = total;
    crit = true;
    weakSpot = true;
    tgt.marked = false;
    tgt.markReservedBy = null;
  }
  // Q49 (Fabi, Variante a): Gründlich gilt für alles, was der Waschbär tut. Auch von seinen Treffern wirkt ein Teil nach.
  if (isHit && src && src.passive === 'gruendlich' && src.side !== tgt.side && amount > EPS) {
    const later = amount * (1 - param(src, 'gruendlich', 'instantShare'));
    const ticks = Math.max(1, Math.round(param(src, 'gruendlich', 'overSeconds')));
    amount -= later;
    tgt.dots.push({ source: src.uid, perTick: later / ticks, ticksLeft: ticks, nextTick: b.t + 1, via: 'passive:gruendlich' });
  }
  // F35 Stachelkugel: Ein Teil des Treffers geht an den Angreifer zurück, gerechnet vor der eigenen Abwehr
  // F35 Stachelhaut (Fabi): Die Stacheln schützen dauerhaft, ein kleiner Teil geht immer zurück
  if (isHit && src && src.alive && perk(tgt, 'thorns') > 0 && tgt.spikyUntil <= b.t) {
    const back = amount * perk(tgt, 'thorns');
    if (back > EPS) dealDamage(b, tgt, src, back, 'reflect', viaOf(tgt, 'thorns'));
  }
  if (isHit && src && src.alive && tgt.spikyUntil > b.t) {
    const back = amount * param(tgt, tgt.kniff ?? 'dornenkugel', 'reflect');
    if (back > EPS) dealDamage(b, tgt, src, back, 'reflect', `kniff:${tgt.kniff ?? 'dornenkugel'}`);
  }
  // S8 Hautgift (Runde 7): Wer den Feuersalamander aus der Nähe angreift, bekommt Gift ab
  // Runde 8 (Fabi): alle, die ihn angreifen, auch aus der Ferne, pro Angriff
  if (isHit && src && src.alive && src.side !== tgt.side && tgt.toxicUntil > b.t) {
    addPoison(b, tgt, src, Math.round(param(tgt, 'hautgift', 'stacks')));
  }
  if (tgt.enragedUntil > b.t) amount *= getTrait('wuetend').params.damageTakenFactor!;
  // B25: Nebeneinander stecken Verbundene etwas weniger ein
  // S13 Pfote halten: Solange der Freund nah ist, nehmen beide weniger Schaden (höchstens die Hälfte weniger)
  const paw = pawOf(b, tgt);
  if (paw > 0 && amount > EPS) {
    tgt.stats.shielded += amount * paw;
    amount *= 1 - paw;
  }
  const bonded = bondOf(b, tgt);
  if (bonded > 0 && amount > EPS) {
    tgt.stats.shielded += (amount * bonded) / 2;
    amount *= 1 - bonded / 2;
  }
  // I31 (Fabi, 17.10.2026 nachts: Items für Raufbolde, die auf Kante spielen)
  // Bernsteintropfen: eingehüllt tut kein Treffer weh, er kämpft weiter
  if (tgt.amberUntil > b.t && amount > EPS) {
    tgt.stats.shielded += amount;
    amount = 0;
  }
  // Wildschweinborste: je weniger Leben, desto weniger steckt er ein; bei vollem Leben mehr
  if (perk(tgt, 'edgeHide') > 0 && amount > EPS) {
    const share = Math.max(0, tgt.hp / tgt.maxHp);
    const factor = EDGE_HIDE_MIN + (1 + perk(tgt, 'edgeHide') - EDGE_HIDE_MIN) * share;
    if (factor < 1) tgt.stats.shielded += amount * (1 - factor);
    amount *= factor;
  }
  // I25 Schneckenhaus (Fabi, Runde 7): Mit wenig Leben zieht sich der Träger ins Haus zurück und steckt weniger ein
  if (perk(tgt, 'shellGuard') > 0 && tgt.hp < tgt.maxHp * SHELL_BELOW) {
    const prevented = amount * perk(tgt, 'shellGuard');
    amount -= prevented;
    tgt.stats.shielded += prevented;
  }
  // F35 Stachelball: Wer kreiselt, steckt etwas weniger ein
  if (tgt.spin && tgt.kniff) amount *= 1 - optParam(tgt, tgt.kniff, 'damageReduction');
  // S5 Bau graben: verhinderter Schaden zählt als Schützen für den Dachs
  if (tgt.guardUntil > b.t) {
    const prevented = amount * tgt.guardReduction;
    amount -= prevented;
    const g = unitById(b, tgt.guardSource);
    if (g) g.stats.shielded += prevented;
  }
  // S5 Dickes Fell: fester Abzug pro Treffer, stark gegen viele kleine Treffer
  if (isHit && tgt.passive === 'dickes-fell') {
    const after = Math.max(param(tgt, 'dickes-fell', 'minDamage'), amount - param(tgt, 'dickes-fell', 'flatReduction'));
    tgt.stats.shielded += amount - after;
    amount = after;
  }
  // F35 Schutzschild: fängt Schaden ab, bevor Leben verloren geht; zählt als Schützen für den, der ihn gab
  let blocked = 0;
  let blockedBy: number | null = null;
  if (tgt.shield > EPS && amount > EPS) {
    const absorbed = Math.min(tgt.shield, amount);
    blocked = absorbed;
    blockedBy = tgt.shieldSource;
    tgt.shield -= absorbed;
    amount -= absorbed;
    const giver = unitById(b, tgt.shieldSource);
    if (giver) giver.stats.shielded += absorbed;
    if (tgt.shield <= EPS) tgt.shield = 0;
  }
  // I31 Bernsteintropfen (Fabi, 17.10.2026 nachts): Der Treffer, der ihn unter 10 % Leben brächte, lässt ihm ein letztes
  // bisschen Leben, dann hüllt ihn der Bernstein ein und eine Weile tut ihm kein Treffer weh; er kämpft weiter
  if (perk(tgt, 'amberGuard') > 0 && !tgt.amberUsed && tgt.kind !== 'tree' && amount > EPS && tgt.hp - amount <= tgt.maxHp * AMBER_BELOW) {
    tgt.amberUsed = true;
    tgt.amberUntil = b.t + perk(tgt, 'amberGuard');
    const keep = Math.min(tgt.hp, Math.max(1, tgt.maxHp * 0.02));
    if (tgt.hp - amount < keep) {
      tgt.stats.shielded += amount - (tgt.hp - keep);
      amount = tgt.hp - keep;
    }
    setStatus(b, tgt, 'amber', tgt.amberUntil, viaOf(tgt, 'amberGuard'));
  }
  const actual = Math.min(amount, tgt.hp);
  // I28 Fingerhutkranz (Fabi, Runde 8): Kniffe hinterlassen zusätzlich Gift, das bis zum Kampfende bleibt
  if (kind === 'kniff' && src && src.side !== tgt.side && perk(src, 'kniffVenom') > 0 && tgt.kind !== 'tree' && actual > EPS) {
    tgt.dots.push({ source: src.uid, perTick: (actual * perk(src, 'kniffVenom')) / VENOM_PAYBACK, ticksLeft: Number.MAX_SAFE_INTEGER, nextTick: b.t + 1, venom: true, via: viaOf(src, 'kniffVenom') });
  }
  tgt.hp -= actual;
  tgt.stats.taken += actual;
  if (actual > EPS) {
    tgt.hitLog.push({ t: b.t, amount: actual });
    if (tgt.hitLog.length > 40) tgt.hitLog = tgt.hitLog.filter((h) => h.t > b.t - RECENT_SECONDS - EPS);
  }
  if (src) {
    // Anteil der Schwachstelle am Treffer geht an den Eisvogel
    const bonusPart = bonus > 0 ? (actual * bonus) / (beforeMark + bonus) : 0;
    const marker = unitById(b, tgt.markedBy);
    if (bonusPart > 0 && marker) marker.stats.damage += bonusPart;
    src.stats.damage += actual - (marker ? bonusPart : 0);
  }
  // K6/K20: Eingesteckte Treffer füllen den Mut ein wenig
  if (tgt.kind !== 'tree') gainMut(b, tgt, (b.rules.mut.perMaxHpTaken * actual) / tgt.maxHp);
  // Fabi (Phase Kern): Was der Schild abfängt, steht dabei, damit „nimmt 0 Schaden“ einen Grund hat
  const shown = round(blocked) > 0 ? { blocked: round(blocked), ...(blockedBy !== null ? { blockedBy } : {}) } : {};
  emit(b, { t: round(b.t), type: 'damage', from: src?.uid ?? null, to: tgt.uid, amount: round(actual), source: kind, weakSpot, crit, ...(how ? { via: how } : {}), ...shown, ...(spotOpen ? { spotOpen: true } : {}) });
  if (weakSpot && !tgt.markHold) tgt.markedBy = null;
  // K52: Trifft ein Kniff die Schwachstelle, stürzt der Eisvogel hinterher
  if (spotOpen && onSpotOpen) onSpotOpen(b, tgt);
  // I5 Moorbeere: Der Träger verletzt sich mit einem Teil des Schadens, den er macht
  if (src && isHit && src !== tgt && perk(src, 'selfDamage') > 0 && src.alive && actual > EPS) {
    dealDamage(b, null, src, actual * perk(src, 'selfDamage'), 'recoil', viaOf(src, 'selfDamage'));
  }
  // I19 (Fabi, Spieltest Phase 4): Sich stärken. Wer trifft, holt sich einen Teil des Schadens als Leben zurück
  // F35 Zähe Maus (Vorschlag): im Heißhunger stärkt sich die Wasserspitzmaus mit jedem Biss
  const rage = !!src && src.kniff === 'raserei' && src.empowerUntil > b.t;
  // I31 Brombeerranke: auf der Kante (unter einem Drittel Leben) stärkt sich der Träger mit jedem Treffer
  const edge = !!src && perk(src, 'edgeLifesteal') > 0 && src.hp < src.maxHp * EDGE_BELOW;
  const steal = src ? perk(src, 'lifesteal') + (rage ? perk(src, 'rageLifesteal') : 0) + (edge ? perk(src, 'edgeLifesteal') : 0) : 0;
  if (src && isHit && src !== tgt && steal > 0 && src.alive && actual > EPS) {
    // Fabi (Phase Kern): Im Verlauf steht, womit er sich heilt, etwa mit der Holunderkette und durch Heißhunger
    const from = [viaOf(src, 'lifesteal'), rage && perk(src, 'rageLifesteal') > 0 ? [viaOf(src, 'rageLifesteal'), 'kniff:raserei'].filter(Boolean).join('|') : undefined, edge ? viaOf(src, 'edgeLifesteal') : undefined];
    applyHeal(b, src, src, actual * steal, from.filter(Boolean).join('|') || undefined);
  }

  if (tgt.hp <= EPS) {
    down(b, tgt, src);
    return;
  }
  checkThresholds(b, tgt);
}

/** K52: Wer auf eine geöffnete Schwachstelle reagiert (der Sturzflug); gesetzt von orders.ts, damit core.ts klein bleibt */
let onSpotOpen: ((b: Battle, tgt: Unit) => void) | null = null;
export function setSpotOpenHandler(fn: (b: Battle, tgt: Unit) => void): void {
  onSpotOpen = fn;
}

/** Nusshagel (Fabi, Phase Kern): so lange fliegt die Nuss vom Ziel zum Freund [ZAHL?] */
export const NUT_BOUNCE_SECONDS = 0.3;
/** I25 Schneckenhaus: unter diesem Anteil Leben [ZAHL?] */
export const SHELL_BELOW = 0.3;
/** I25 Staubpilz: bei so wenig Leben platzt er [ZAHL?] */
export const SPORE_BELOW = 0.1;
/** I31 Bernsteintropfen: bei so wenig Leben hüllt er ein [ZAHL?] */
export const AMBER_BELOW = 0.1;
/** I31 Wildschweinborste: so viel vom Schaden kommt kurz vor dem Fallen noch an [ZAHL?] */
export const EDGE_HIDE_MIN = 0.7;
/** I31 Brombeerranke: unter diesem Anteil Leben ist er auf der Kante [ZAHL?] */
export const EDGE_BELOW = 1 / 3;
/** I25 Fingerhut: nach so vielen Sekunden hat das bleibende Gift den Anteil wieder eingebracht, danach ist es Gewinn [ZAHL?] */
export const VENOM_PAYBACK = 8;

export function checkThresholds(b: Battle, u: Unit): void {
  const share = u.hp / u.maxHp;
  // I25 Staubpilz (Fabi, Runde 7): Bei ganz wenig Leben platzt er, die Sporenwolke nebelt kurz ein;
  // Runde 8 (Fabi): nur die, die den Träger gerade angreifen, sonst wäre jeder zwei Sekunden lang sicher
  if (perk(u, 'sporeCloud') > 0 && !u.sporeUsed && share <= SPORE_BELOW) {
    u.sporeUsed = true;
    for (const e of b.units) {
      if (!e.alive || e.side === u.side || e.kind === 'tree' || e.targetUid !== u.uid) continue;
      e.stunnedUntil = Math.max(e.stunnedUntil, b.t + perk(u, 'sporeCloud'));
      e.targetUid = null;
      setStatus(b, e, 'stunned', e.stunnedUntil, viaOf(u, 'sporeCloud'));
    }
    emit(b, { t: round(b.t), type: 'spores', unit: u.uid });
  }
  // S5 Igel: Hat er zwei Drittel des Lebens verloren, mit dem er ans Ufer kam, rollt er sich einmal ein; Angreifer
  // verlieren den Fokus. Spieltest Phase 4 (Fabi): gemessen am Leben zu Kampfbeginn, nicht am vollen Leben
  if (u.passive === 'einrollen' && !u.rollUsed && perk(u, 'noCurl') <= 0 && u.hp <= u.startHp * param(u, 'einrollen', 'hpThreshold') + EPS) {
    u.rollUsed = true;
    u.rolledUntil = b.t + param(u, 'einrollen', 'duration');
    setStatus(b, u, 'rolled', u.rolledUntil, 'passive:einrollen');
    for (const e of b.units) if (e.alive && e.targetUid === u.uid && !e.traits.includes('fanatisch')) e.targetUid = null;
  }
  // K23 Feige: flieht bei wenig Leben an den Rand und kommt zurück
  if (u.traits.includes('feige') && !u.fleeUsed && share <= getTrait('feige').params.hpThreshold!) {
    u.fleeUsed = true;
    u.fleeUntil = Infinity; // bis er entkommt oder fällt
    u.targetUid = null;
    setStatus(b, u, 'fleeing', b.t, 'trait:feige');
  }
  // K23 Wütend: bei einem Viertel Leben eine Weile zäher und stärker
  if (u.traits.includes('wuetend') && !u.rageUsed && share <= getTrait('wuetend').params.hpThreshold!) {
    u.rageUsed = true;
    u.enragedUntil = b.t + getTrait('wuetend').params.duration!;
    setStatus(b, u, 'enraged', u.enragedUntil, 'trait:wuetend');
  }
}

/** K36 (Fabi, Phase Kern): Wer mit einem Sprung direkt vor einem Gegner landet und ihn trifft, zieht ihn an */
export function provoke(b: Battle, u: Unit, victim: Unit): void {
  if (!victim.alive || victim.side === u.side || victim.kind === 'tree' || victim.traits.includes('fanatisch')) return;
  if (victim.tauntBy !== null && victim.tauntAttacks > 0) return; // ein Spott geht vor
  // K36 (Fabi, Phase Kern, 17.10.2026 nachts): Wer schon einen Freund in Reichweite angreift, bleibt bei ihm
  const current = unitById(b, victim.targetUid);
  if (current && current !== u && current.alive && targetable(b, current) && gap(victim, current) <= victim.range + EPS) return;
  victim.targetUid = u.uid;
  victim.engagedUid = u.uid;
}

export function down(b: Battle, u: Unit, by: Unit | null): void {
  // F22 Versprechen: wirkt nicht mehr im Kampf, sondern danach im Run (Q43, „Ich komm nach“)
  // F35 Streugift: Fällt ein vergifteter Gegner, springt sein ganzer Stapel auf den nächsten über
  const poisoner = unitById(b, u.poison.source);
  if (u.poison.stacks > 0 && poisoner && perk(poisoner, 'poisonJump') > 0) {
    const next = nearest(u, b.units.filter((o) => o !== u && o.side === u.side && o.alive && o.kind !== 'tree'));
    if (next) {
      const stacks = u.poison.stacks;
      u.poison.stacks = 0;
      u.poison.expiries = [];
      u.poison.owners = [];
      // Spieltest Phase 4 (Fabi, wie Malzahars E): Das Gift springt auf genau einen weiter und addiert sich dort nicht
      // auf; der neue Träger hat danach so viele Stapel wie der alte, wenn er nicht schon mehr hatte
      const add = stacks - next.poison.stacks;
      if (add > 0) addPoison(b, poisoner, next, add);
      // Spieltest Phase 4 (Fabi): Das Überspringen steht im Verlauf
      emit(b, { t: round(b.t), type: 'poisonJump', from: u.uid, to: next.uid, stacks, source: poisoner.uid });
    }
  }
  u.alive = false;
  u.hp = 0;
  u.leap = null;
  u.dive = null;
  u.travel = null;
  u.slide = null;
  u.swoop = null;
  u.order = null;
  // K48: Wer fällt, bringt seinen angesagten Angriff nicht mehr an
  if (u.windup) {
    emit(b, { t: round(b.t), type: 'telegraphEnd', id: u.windup.id, unit: u.uid, done: false });
    u.windup = null;
  }
  for (const o of b.units) if (o.markReservedBy === u.uid) o.markReservedBy = null;
  emit(b, { t: round(b.t), type: 'down', unit: u.uid, by: by?.uid ?? null });
  if (u.side === 'enemy' && by && by.side === 'friend') by.stats.kills += 1;
  // F21, F42: gefallene Freunde; ob einer Abschied nimmt, entscheidet die Bilanz (verwundet oder nicht)
  if (u.kind === 'friend') b.farewells += 1;
  // S5: Fällt der Baum, steht das Eichhörnchen ohne da
  if (u.kind === 'tree') {
    for (const s of b.units) if (s.treeUid === u.uid && s.alive) s.onTree = false;
  }
  for (const o of b.units) if (o.targetUid === u.uid) o.targetUid = null;
}

/** K23 Feige: entkommen, der Kampf geht ohne ihn weiter, der XP-Topf wird kleiner */
/**
 * K21 (Fabi, Spieltest Phase 4): Wind und Stöße schieben Tiere über den Rand. Am Rand stemmen sie sich dagegen; erst
 * wenn sie dort genug abbekommen haben, sind sie draußen. Wer draußen ist, wartet am Rand wie einer, den man nicht
 * aufgestellt hat: kein Abschied, keine XP, nach dem Kampf mit seinem Leben zurück an Bord.
 */
export function shove(b: Battle, u: Unit, dx: number, dy: number): void {
  const f = b.rules.field;
  const x = u.x + dx;
  const y = u.y + dy;
  const over = Math.max(0, u.radius - x, x - (f.width - u.radius)) + Math.max(0, u.radius - y, y - (f.height - u.radius));
  u.x = x;
  u.y = y;
  clampToField(b, u);
  if (over <= EPS || u.kind !== 'friend' || !u.alive) return;
  // K33 (Phase Kern): „Über den Rand“ ausgeblendet; geschubst wird weiter, hinaus fällt niemand
  if (!systemOn('edgeOut')) return;
  u.edgeStrain += over;
  if (u.edgeStrain >= b.rules.edge.grip) pushOut(b, u);
}

export function pushOut(b: Battle, u: Unit): void {
  u.alive = false;
  u.escaped = true;
  u.pushedOut = true;
  u.leap = null;
  u.spin = null;
  u.march = null;
  emit(b, { t: round(b.t), type: 'pushedOut', unit: u.uid });
  for (const o of b.units) if (o.targetUid === u.uid) o.targetUid = null;
}

export function escape(b: Battle, u: Unit): void {
  u.alive = false;
  u.leap = null;
  u.escaped = true;
  u.fleeUntil = 0;
  emit(b, { t: round(b.t), type: 'escape', unit: u.uid });
  for (const o of b.units) if (o.targetUid === u.uid) o.targetUid = null;
}

export function heal(b: Battle, src: Unit, tgt: Unit, amount: number, via?: string, bounce?: number): void {
  const how = via ?? b.via ?? undefined;
  if (!tgt.alive) return;
  // K46 Übungsufer: Heilung wächst mit dem Leben mit
  amount *= b.setup.hpFactor ?? 1;
  // S5 Waschbär, Gründlich: Ein Teil der Heilung wirkt nach
  if (src.passive === 'gruendlich') {
    const instant = amount * param(src, 'gruendlich', 'instantShare');
    const seconds = param(src, 'gruendlich', 'overSeconds');
    const rest = amount - instant;
    if (rest > 0) tgt.hots.push({ perTick: rest / seconds, ticksLeft: seconds, nextTick: b.t + 1, source: src.uid, ...(how ? { via: how } : {}) });
    amount = instant;
  }
  applyHeal(b, src, tgt, amount, how, bounce);
}

export function applyHeal(b: Battle, src: Unit, tgt: Unit, amount: number, via?: string, bounce?: number): void {
  const actual = Math.min(amount, tgt.maxHp - tgt.hp);
  if (actual <= EPS) return;
  tgt.hp += actual;
  src.stats.healed += actual;
  const how = via ?? b.via ?? undefined;
  emit(b, { t: round(b.t), type: 'heal', from: src.uid, to: tgt.uid, amount: round(actual), ...(how ? { via: how } : {}), ...(bounce !== undefined ? { bounce } : {}) });
  // I25 Distelbalsam (Fabi, Runde 7): Ein Teil der Heilung piekst den Gegner, der dem Geheilten am nächsten steht
  if (perk(src, 'healStrike') > 0) {
    const foe = nearest(tgt, b.units.filter((o) => o.side !== src.side && targetable(b, o) && o.kind !== 'tree'));
    if (foe) dealDamage(b, src, foe, actual * perk(src, 'healStrike'), 'thistle', viaOf(src, 'healStrike'));
  }
}

export function addPoison(b: Battle, src: Unit, tgt: Unit, stacks: number): void {
  if (!tgt.alive) return;
  // F4: Giftstapel addieren sich. F32 (Spieltest Phase 4): Jeder Stapel verfällt nach seiner eigenen Dauer
  if (tgt.poison.stacks === 0) tgt.poison.nextTick = b.t + b.rules.poisonTickSeconds;
  // F35 Streugift: Stapel verfallen nicht mehr. F44 Brennendes Gift (Feuersalamander als Giftzahn): seine Stapel wirken kürzer
  const burn = perk(src, 'poisonBurnSeconds');
  const end = perk(src, 'poisonPersist') > 0 ? Infinity : b.t + (burn > 0 ? burn : param(src, 'giftiger-biss', 'duration'));
  for (let i = 0; i < stacks; i++) {
    tgt.poison.expiries.push(end);
    tgt.poison.owners.push(src.uid);
  }
  tgt.poison.stacks = tgt.poison.expiries.length;
  tgt.poison.source = src.uid;
  emit(b, { t: round(b.t), type: 'poison', unit: tgt.uid, stacks: tgt.poison.stacks });
}

// ---------- Angriffe und Kniffe ----------

/** B25: Steht ein Verbundener aus dem Gemeinschaftsraum nah genug neben ihm? Dann gilt das Band */
/** S13 Pfote halten: wie viel weniger Schaden gerade, wenn einer der Pfote-Freunde nah genug ist */
export function pawOf(b: Battle, u: Unit): number {
  if (!u.paw || !u.alive) return 0;
  const near = u.paw.with.some((uid) => {
    const o = unitById(b, uid);
    return !!o && o.alive && gap(u, o) <= u.paw!.reach;
  });
  return near ? Math.min(0.5, u.paw.reduction) : 0;
}

export function bondOf(b: Battle, u: Unit): number {
  if (!u.bond || !u.alive) return 0;
  const near = u.bond.with.some((uid) => {
    const o = unitById(b, uid);
    return !!o && o.alive && gap(u, o) <= u.bond!.reach;
  });
  return near ? u.bond.bonus : 0;
}

export function hitDamage(b: Battle, u: Unit, factor = 1): number {
  const empower = u.empowerUntil > b.t ? u.empowerFactor : 1;
  // G26 (Fabi, Phase Kern): Ohne Versteck kämpft das Nieselchen mit dem Blattschwert aus der Nähe, etwas stärker
  const melee = !u.hide ? 1 : u.hideMelee ? u.hide.meleeBoost : u.pounce ? 1 : (u.hide.throwFactor ?? 1);
  // K41 Trotz (Fabi, Phase Kern, Versuch): Ein Raufbold schlägt kräftiger zu, je weniger Leben er hat
  const fury = u.friendClass ? (classTrait(u.friendClass)?.lowHpDamage ?? 0) : 0;
  const grit = 1 + fury * (1 - Math.max(0, u.hp) / u.maxHp);
  // B25 (Fabi, 29.10.2026): Verbundene aus dem Gemeinschaftsraum kämpfen nebeneinander besser
  const bond = 1 + bondOf(b, u);
  return u.damage * factor * outgoingFactor(b, u) * empower * melee * grit * bond * b.rainMultiplier;
}

export function gainMut(b: Battle, u: Unit, amount: number): void {
  // I1 Mutstein, F35: Mut füllt sich schneller
  u.mut = Math.min(u.mutMax, u.mut + amount * (u.perks.mutGain ?? 1));
}

export function attack(b: Battle, u: Unit, tgt: Unit, opts: { mut?: boolean } = {}): void {
  u.engagedUid = tgt.uid;
  // T31: Daran hängt die Darstellung den Schlag oder Wurf, damit er im Bild sitzt, wenn der Treffer zählt
  emit(b, { t: round(b.t), type: 'strike', unit: u.uid, target: tgt.uid, kind: 'hit' });
  // G22, G24 (Fabi, Runde 7): Mit Dreck in den Augen oder eingeschüchtert trifft man eine Weile schwächer
  const weak = u.weakAttacks > 0 ? u.weakFactor : 1;
  if (u.weakAttacks > 0 && --u.weakAttacks <= 0) {
    u.weakFactor = 1;
    u.weakKind = null;
  }
  const raw = hitDamage(b, u) * weak;
  // S5 Stachelkugel: Wer verspottet ist, zählt seine Angriffe auf den Igel herunter
  if (u.tauntBy !== null && tgt.uid === u.tauntBy) {
    u.tauntAttacks -= 1;
    if (u.tauntAttacks <= 0) u.tauntBy = null;
  }
  // K47: Fernangriffe fliegen als Geschoss; was der Treffer auslöst, passiert erst, wenn es ankommt
  const art = b.setup.projectiles ? rangedArt(b, u, tgt) : null;
  if (art) launchHit(b, u, tgt, raw, art);
  else landHit(b, u, tgt, raw);
  if (opts.mut !== false) gainMut(b, u, u.mutPerAttack);
}

/** Was ein Angriff anrichtet, wenn er ankommt: Schaden, Gift, Splitter, Nüsse (aus attack, K47) */
export function landHit(b: Battle, u: Unit, tgt: Unit, raw: number): void {
  if (!tgt.alive) return;
  // I25 Fingerhut (Fabi, Runde 7): Ein Teil des Treffers wird zu Gift, das nicht verfliegt; je länger der Kampf, desto mehr
  const venom = perk(u, 'venom');
  if (venom > 0 && tgt.kind !== 'tree') {
    const part = raw * venom;
    raw -= part;
    tgt.dots.push({ source: u.uid, perTick: part / VENOM_PAYBACK, ticksLeft: Number.MAX_SAFE_INTEGER, nextTick: b.t + 1, venom: true, via: viaOf(u, 'venom') });
  }
  // I25 Bienenstich (Fabi, Runde 7): Die Angriffe vergiften zusätzlich, ein Anteil des Treffers über fünf Sekunden
  const sting = perk(u, 'sting');
  if (sting > 0 && tgt.kind !== 'tree') tgt.dots.push({ source: u.uid, perTick: (raw * sting) / 5, ticksLeft: 5, nextTick: b.t + 1, venom: true, via: viaOf(u, 'sting') });
  dealDamage(b, u, tgt, raw, 'hit');
  // R27 Waschbär als Adlerauge: Die Kiesel zerschellen und treffen auch, wer daneben steht
  if (perk(u, 'splash') > 0) splash(b, u, tgt, raw * perk(u, 'splash'), perk(u, 'splashRadius') || 5, viaOf(u, 'splash'));
  if (u.passive === 'giftiger-biss') addPoison(b, u, tgt, param(u, 'giftiger-biss', 'stacksPerAttack'));
  // S8 Feuersalamander als Giftzahn: Jeder Treffer legt Gift
  if (perk(u, 'poisonHits') > 0) addPoison(b, u, tgt, Math.round(perk(u, 'poisonHits')));
  // Nusshagel (Fabi): Jede Nuss, die trifft, prallt ab und heilt den Freund, der dem Ziel am nächsten steht.
  // Fabi (Phase Kern): erst ins Ziel, von dort zum Freund; die Heilung kommt an, wenn die Nuss gelandet ist
  if (perk(u, 'nutHeal') > 0) {
    const mate = nearest(tgt, b.units.filter((o) => o.side === u.side && o.alive && o.kind !== 'tree' && o.hp < o.maxHp - EPS));
    if (mate) b.bounces.push({ at: b.t + NUT_BOUNCE_SECONDS, src: u.uid, from: tgt.uid, to: mate.uid, amount: perk(u, 'nutHeal'), via: viaOf(u, 'nutHeal') });
  }
}

export function splash(b: Battle, u: Unit, center: { x: number; y: number }, amount: number, radius: number, via?: string): void {
  for (const o of b.units) {
    if (o === center || o.side === u.side || !targetable(b, o) || o.kind === 'tree') continue;
    if (dist(o, center) - o.radius <= radius + EPS) dealDamage(b, u, o, amount, 'splash', via);
  }
}

/** Wie viel Schaden ein Freund in den letzten Sekunden eingesteckt hat (Fabi: Schutzpatron, Vorratskammer) */
export const RECENT_SECONDS = 3;
export function recentDamage(b: Battle, u: Unit, seconds = RECENT_SECONDS): number {
  return u.hitLog.reduce((a, h) => (h.t > b.t - seconds - EPS ? a + h.amount : a), 0);
}

/** Wer gerade am meisten abbekommt; ohne Treffer der, dem es am schlechtesten geht */
export function hardestHit(b: Battle, list: Unit[], seconds = RECENT_SECONDS): Unit[] {
  return [...list].sort((a, c) => recentDamage(b, c, seconds) - recentDamage(b, a, seconds) || a.hp / a.maxHp - c.hp / c.maxHp || a.uid - c.uid);
}

/** Wenigstes Leben; bei Gleichstand der Nähere, damit die Aufstellung zählt (Fabi, Spieltest Phase 4) */
export function weakestOf(u: Unit, list: Unit[]): Unit | undefined {
  return list.reduce<Unit | undefined>((a, o) => (!a || o.hp < a.hp - EPS || (Math.abs(o.hp - a.hp) <= EPS && dist(u, o) < dist(u, a)) ? o : a), undefined);
}

export function lowestHpEnemy(b: Battle, u: Unit): Unit | undefined {
  return weakestOf(u, b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree'));
}

export function mostHurtFriend(b: Battle, u: Unit, byShare: boolean): Unit | undefined {
  let best: Unit | undefined;
  let bestScore = 0;
  for (const o of b.units) {
    if (o.side !== u.side || !o.alive || o.kind === 'tree' || o.hp >= o.maxHp - EPS) continue;
    const score = byShare ? 1 - o.hp / o.maxHp : o.maxHp - o.hp;
    if (score > bestScore + EPS) {
      best = o;
      bestScore = score;
    }
  }
  return best;
}

/** Stellt u direkt neben das Ziel, auf die Seite, von der es kommt. */
export function placeNextTo(b: Battle, u: Unit, tgt: Unit): void {
  const d = dist(u, tgt);
  const nx = d > EPS ? (u.x - tgt.x) / d : -1;
  const ny = d > EPS ? (u.y - tgt.y) / d : 0;
  const r = tgt.radius + u.radius + 0.5;
  u.x = tgt.x + nx * r;
  u.y = tgt.y + ny * r;
  clampToField(b, u);
}
